import showUpdateMessage from "~/utils/showUpdateMessage";

// One failure, five wordings: the browsers disagree on how to phrase a failed
// module import, and Vite words a failed stylesheet differently again.
const CHUNK_LOAD_ERRORS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "'text/html' is not a valid JavaScript MIME type",
  "Unable to preload CSS",
];

const RELOAD_TIMESTAMP_KEY = "chunkLoadReloadedAt";
// Long enough that a reload which fails the same way is recognised as a repeat,
// short enough that the next incident weeks later starts from a clean slate.
const RELOAD_COOLDOWN_MS = 30_000;

export function isChunkLoadError(error) {
  const message = typeof error === "string" ? error : (error?.message ?? "");

  return CHUNK_LOAD_ERRORS.some((chunkLoadError) => message.includes(chunkLoadError));
}

export function recoverFromChunkLoadError() {
  const reloadedAt = lastReloadAt();
  const canReload = reloadedAt !== null && Date.now() - reloadedAt >= RELOAD_COOLDOWN_MS;

  if (canReload && rememberReload()) {
    window.location.reload();
    return;
  }

  showUpdateMessage(__FRONTEND_VERSION__); // eslint-disable-line no-undef
}

// Vite dispatches this for every chunk the preload helper could not fetch — the
// module itself and the stylesheets linked alongside it. Nothing else sees
// those: a lazy route component rejects inside vue-router, so the failure never
// reaches Vue's errorHandler and surfaces as an unhandled rejection instead.
export function installChunkLoadRecovery() {
  window.addEventListener("vite:preloadError", recoverFromChunkLoadError);
}

// Null means the browser denies us storage (private mode, blocked cookies).
// Without it a repeat is indistinguishable from a first failure, and reloading
// blind would loop.
function lastReloadAt() {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY)) || 0;
  } catch {
    return null;
  }
}

function rememberReload() {
  try {
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}
